import {
  Agent,
  NavigationStructure,
  AgentConfig,
  InstructionSet,
  InstructionTemplate,
  KnowledgeSource,
  AgentTools,
  AgentWidgets,
  AgentHistory,
  CostData,
} from '../types'

export const mockAgents: Agent[] = [
  {
    id: '1',
    name: 'Service 1',
    type: 'Matcher',
    shortDescription:
      'Analizza i profili utente e calcola punteggi di affinità per suggerire connessioni rilevanti.',
    examplePrompt: 'Trova utenti con interessi simili ai miei',
    exampleResponse:
      'Ho trovato 5 utenti con alta affinità: Maria Rossi (95%), Luca Bianchi (92%), Sofia Verdi (88%)...',
  },
  {
    id: '2',
    name: 'Service 2',
    type: 'Chatbot',
    shortDescription:
      'Assistente conversazionale completo per rispondere a domande generali sulla piattaforma.',
    examplePrompt: 'Come posso iscrivermi a un corso?',
    exampleResponse:
      'Per iscriverti a un corso, vai alla sezione "Catalogo", seleziona il corso desiderato e clicca su "Iscriviti". Ti guiderò passo dopo passo!',
  },
  {
    id: '3',
    name: 'Service 3',
    type: 'Tutor',
    shortDescription:
      'Tutor personalizzato che aiuta gli studenti a comprendere i contenuti dei corsi con spiegazioni su misura.',
    examplePrompt: "Spiegami il principio di conservazione dell'energia",
    exampleResponse:
      "Il principio di conservazione dell'energia afferma che l'energia totale di un sistema isolato rimane costante. Posso farti un esempio pratico con un pendolo...",
  },
  {
    id: '4',
    name: 'Service 4',
    type: 'Enhancer',
    shortDescription:
      'Suggerisce miglioramenti al profilo utente per aumentare visibilità e opportunità di networking.',
    examplePrompt: 'Come posso migliorare il mio profilo?',
    exampleResponse:
      'Il tuo profilo è completo al 65%. Ti consiglio di aggiungere: 1) Una foto professionale, 2) Le tue competenze chiave, 3) Almeno 3 progetti...',
  },
  {
    id: '5',
    name: 'Service 5',
    type: 'Commenter',
    shortDescription:
      'Genera commenti pertinenti e costruttivi nelle discussioni di gruppo per stimolare il dialogo.',
    examplePrompt: 'Commenta questa discussione sulla sostenibilità',
    exampleResponse:
      "Ottimo punto sulla circolarità! Aggiungerei che le aziende devono anche considerare l'impatto sociale oltre a quello ambientale...",
  },
  {
    id: '6',
    name: 'Service 6',
    type: 'Note-taker',
    shortDescription:
      'Prende appunti automatici durante le videochiamate e genera riassunti strutturati.',
    examplePrompt: 'Riassumi la riunione di oggi',
    exampleResponse:
      'Riunione del 15/01/2024 - Durata: 45 min. Argomenti: 1) Revisione Q4, 2) Obiettivi Q1, 3) Budget marketing. Azioni: Maria prepara report entro venerdì...',
  },
  {
    id: '7',
    name: 'Service 7',
    type: 'Summarizer',
    shortDescription:
      "Crea riassunti periodici delle attività dell'utente sulla piattaforma con insights e statistiche.",
    examplePrompt: 'Mostrami il riepilogo settimanale',
    exampleResponse:
      'Questa settimana hai: completato 3 moduli, partecipato a 5 discussioni, ottenuto 2 certificati. Il tuo tempo di studio è aumentato del 15%!',
  },
  {
    id: '8',
    name: 'Service 8',
    type: 'Reporter',
    shortDescription:
      'Genera report dettagliati su metriche, progressi e performance con visualizzazioni e analisi.',
    examplePrompt: 'Crea un report sui miei progressi',
    exampleResponse:
      'Report Progressi - Gennaio 2024: Corsi completati: 4/6 (67%), Media voti: 8.5/10, Tempo totale: 32 ore. Trend positivo rispetto a dicembre (+12%).',
  },
]

export const navigationStructure: NavigationStructure = {
  groups: [
    {
      title: 'Panoramica',
      items: [
        { id: 'overview', label: 'Panoramica' },
        { id: 'regole', label: 'Regole' },
      ],
    },
    {
      title: 'Gestione',
      items: [
        { id: 'servizi', label: 'Servizi' },
        { id: 'knowledge', label: 'Knowledge' },
        { id: 'funzionalita', label: 'Funzionalità' },
        { id: 'widgets', label: 'Widgets' },
      ],
    },
    {
      title: 'Monitoraggio',
      items: [
        { id: 'utenti', label: 'Utenti' },
        { id: 'insights', label: 'Insights' },
      ],
    },
  ],
}

export const mockUsers = [
  {
    id: '1',
    nome: 'Marco',
    cognome: 'Rossi',
    email: 'marco.rossi@example.com',
    note: 'Utente premium',
  },
  {
    id: '2',
    nome: 'Sofia',
    cognome: 'Bianchi',
    email: 'sofia.bianchi@example.com',
    note: 'Account aziendale',
  },
  {
    id: '3',
    nome: 'Luca',
    cognome: 'Verdi',
    email: 'luca.verdi@example.com',
    note: '',
  },
  {
    id: '4',
    nome: 'Anna',
    cognome: 'Neri',
    email: 'anna.neri@example.com',
    note: 'Trial attivo',
  },
  {
    id: '5',
    nome: 'Giovanni',
    cognome: 'Russo',
    email: 'giovanni.russo@example.com',
    note: 'Supporto prioritario',
  },
  {
    id: '6',
    nome: 'Giulia',
    cognome: 'Ferrari',
    email: 'giulia.ferrari@example.com',
    note: '',
  },
  {
    id: '7',
    nome: 'Alessandro',
    cognome: 'Esposito',
    email: 'alessandro.esposito@example.com',
    note: 'Admin',
  },
]

export const mockAgentConfigs: Record<string, AgentConfig> = {
  '1': {
    agentId: '1',
    modelName: 'gpt-4-turbo',
    provider: 'OpenAI',
    role: 'Analista di affinità',
    creativityLevel: 2,
    lastUpdated: '12 Ott 2023',
    decisionFlow: [
      {
        id: 'n1',
        label: 'Input Utente',
        type: 'start',
        description: 'Ricezione dati profilo utente',
        nextNodeIds: ['n2'],
      },
      {
        id: 'n2',
        label: 'Analisi Dati',
        type: 'classification',
        description: 'Estrazione feature rilevanti dal profilo',
        nextNodeIds: ['n3'],
      },
      {
        id: 'n3',
        label: 'Calcolo Score',
        type: 'tool',
        description: 'Esecuzione algoritmo di matching',
        nextNodeIds: ['n4'],
      },
      {
        id: 'n4',
        label: 'Output',
        type: 'output',
        description: 'Restituzione punteggio di affinità',
        nextNodeIds: [],
      },
    ],
  },
  '3': {
    agentId: '3',
    modelName: 'gpt-4.1-mini',
    provider: 'OpenAI',
    role: 'Tutor contenuti',
    creativityLevel: 3,
    lastUpdated: '24 Nov 2023',
    decisionFlow: [
      {
        id: 'n1',
        label: 'Domanda Studente',
        type: 'start',
        description: 'Ricezione query dello studente',
        nextNodeIds: ['n2'],
      },
      {
        id: 'n2',
        label: 'Classificazione',
        type: 'classification',
        description: 'Identificazione argomento e difficoltà',
        nextNodeIds: ['n3'],
      },
      {
        id: 'n3',
        label: 'Recupero Conoscenza',
        type: 'knowledge',
        description: 'Ricerca nella knowledge base del corso',
        nextNodeIds: ['n4'],
      },
      {
        id: 'n4',
        label: 'Generazione Risposta',
        type: 'output',
        description: 'Creazione spiegazione personalizzata',
        nextNodeIds: [],
      },
    ],
  },
  default: {
    agentId: 'default',
    modelName: 'gpt-3.5-turbo',
    provider: 'OpenAI',
    role: 'Assistente generico',
    creativityLevel: 3,
    lastUpdated: '01 Gen 2024',
    decisionFlow: [
      {
        id: 'n1',
        label: 'Start',
        type: 'start',
        description: 'Punto di ingresso',
        nextNodeIds: ['n2'],
      },
      {
        id: 'n2',
        label: 'Elaborazione',
        type: 'classification',
        description: 'Analisi della richiesta',
        nextNodeIds: ['n3'],
      },
      {
        id: 'n3',
        label: 'Risposta',
        type: 'output',
        description: "Invio risposta all'utente",
        nextNodeIds: [],
      },
    ],
  },
}

export const templatesPremessa: InstructionTemplate[] = [
  {
    id: 'tp1',
    label: 'Template Base',
    content:
      "Sei un assistente AI utile e cordiale. Il tuo obiettivo è aiutare l'utente a risolvere i suoi problemi in modo efficiente.",
  },
  {
    id: 'tp2',
    label: 'Template Formale',
    content:
      'Agisci come un consulente professionale. Mantieni un tono formale, distaccato ma cortese. Usa terminologia tecnica appropriata.',
  },
  {
    id: 'tp3',
    label: 'Template Empatico',
    content:
      "Sei un compagno di supporto empatico. Ascolta attentamente, valida le emozioni dell'utente e offri consigli gentili.",
  },
]

export const templatesLogica: InstructionTemplate[] = [
  {
    id: 'tl1',
    label: 'Step-by-Step',
    content:
      '1. Analizza la richiesta.\n2. Identifica i punti chiave.\n3. Formula una soluzione passo dopo passo.\n4. Verifica la completezza.',
  },
  {
    id: 'tl2',
    label: 'Chain of Thought',
    content:
      "Prima di rispondere, pensa passo dopo passo:\n- Qual è l'intento dell'utente?\n- Quali informazioni mancano?\n- Qual è la migliore strategia di risposta?",
  },
  {
    id: 'tl3',
    label: 'Direct Answer',
    content:
      'Rispondi direttamente alla domanda senza preamboli. Sii conciso e vai dritto al punto.',
  },
]

export const templatesOutput: InstructionTemplate[] = [
  {
    id: 'to1',
    label: 'Markdown Standard',
    content:
      'Formatta la risposta in Markdown. Usa elenchi puntati per la leggibilità e grassetto per i concetti chiave.',
  },
  {
    id: 'to2',
    label: 'JSON Format',
    content:
      'Rispondi ESCLUSIVAMENTE con un oggetto JSON valido che segua questo schema:\n{\n  "answer": "string",\n  "confidence": number,\n  "sources": []\n}',
  },
  {
    id: 'to3',
    label: 'Conversational',
    content:
      'Usa un linguaggio naturale e conversazionale. Evita formattazione complessa. Usa emoji con moderazione.',
  },
]

export const mockInstructionsByAgent: Record<string, InstructionSet> = {
  '3': {
    premessa: {
      currentVersionId: 'v1',
      versions: [
        {
          id: 'v1',
          label: 'V1 - Default',
          content:
            'Sei un tutor esperto in materie scientifiche. Il tuo obiettivo è spiegare concetti complessi in modo semplice.',
          createdAt: '2023-11-20',
        },
        {
          id: 'v2',
          label: 'V2 - Socratico',
          content:
            'Sei un tutor socratico. Non dare mai la risposta diretta, ma guida lo studente con domande.',
          createdAt: '2023-11-25',
        },
      ],
    },
    logica: {
      currentVersionId: 'v1',
      versions: [
        {
          id: 'v1',
          label: 'V1 - Standard',
          content:
            '1. Valuta la comprensione attuale.\n2. Identifica le lacune.\n3. Spiega il concetto mancante.\n4. Verifica la comprensione.',
          createdAt: '2023-11-20',
        },
      ],
    },
    output: {
      currentVersionId: 'v1',
      versions: [
        {
          id: 'v1',
          label: 'V1 - Chiaro',
          content: 'Usa metafore ed esempi pratici. Mantieni le frasi brevi.',
          createdAt: '2023-11-20',
        },
      ],
    },
  },
  default: {
    premessa: {
      currentVersionId: 'v1',
      versions: [
        {
          id: 'v1',
          label: 'V1 - Default',
          content: 'Sei un assistente AI generico.',
          createdAt: '2024-01-01',
        },
      ],
    },
    logica: {
      currentVersionId: 'v1',
      versions: [
        {
          id: 'v1',
          label: 'V1 - Default',
          content: 'Analizza e rispondi.',
          createdAt: '2024-01-01',
        },
      ],
    },
    output: {
      currentVersionId: 'v1',
      versions: [
        {
          id: 'v1',
          label: 'V1 - Default',
          content: 'Rispondi in modo chiaro.',
          createdAt: '2024-01-01',
        },
      ],
    },
  },
}

export const mockKnowledgeSourcesByAgent: Record<string, KnowledgeSource[]> = {
  '3': [
    {
      id: 'ks1',
      name: 'Catalogo corsi 2025',
      type: 'Corso',
      source: 'Database LMS',
      status: 'Attivo',
      lastUpdated: '15 Gen 2024',
      documentsCount: 142,
      tags: ['Formazione', 'Executive', 'Sostenibilità'],
      description:
        "Raccolta completa di tutti i corsi disponibili per l'anno 2025, inclusi programmi executive e corsi sulla sostenibilità.",
      sampleDocuments: [
        {
          id: 'd1',
          title: 'Leadership Sostenibile - Modulo 1',
          snippet:
            'Introduzione ai principi di leadership sostenibile applicati al contesto aziendale moderno...',
          tags: ['Leadership', 'Sostenibilità'],
        },
        {
          id: 'd2',
          title: 'Digital Transformation Executive',
          snippet:
            'Corso avanzato sulla trasformazione digitale per C-level e senior management...',
          tags: ['Executive', 'Digital'],
        },
      ],
    },
    {
      id: 'ks2',
      name: 'FAQ Studenti',
      type: 'FAQ',
      source: 'Upload CSV',
      status: 'Attivo',
      lastUpdated: '10 Gen 2024',
      documentsCount: 87,
      tags: ['Supporto', 'Iscrizioni'],
      description:
        'Domande frequenti degli studenti riguardo iscrizioni, pagamenti, e accesso ai materiali didattici.',
      sampleDocuments: [
        {
          id: 'd3',
          title: 'Come posso iscrivermi a un corso?',
          snippet:
            'Per iscriverti, accedi al portale con le tue credenziali e seleziona il corso desiderato...',
          tags: ['Iscrizioni'],
        },
        {
          id: 'd4',
          title: 'Quali sono i metodi di pagamento accettati?',
          snippet: 'Accettiamo carte di credito, bonifico bancario e PayPal...',
          tags: ['Pagamenti'],
        },
      ],
    },
    {
      id: 'ks3',
      name: 'Profili docenti',
      type: 'Profili utenti',
      source: 'Integrazione esterna',
      status: 'In aggiornamento',
      lastUpdated: '08 Gen 2024',
      documentsCount: 45,
      tags: ['Docenti', 'Expertise'],
      description:
        'Database dei profili dei docenti con le loro competenze, pubblicazioni e aree di specializzazione.',
      sampleDocuments: [
        {
          id: 'd5',
          title: 'Prof. Marco Rossi - Economia Circolare',
          snippet:
            'Esperto in economia circolare con 15 anni di esperienza nel settore...',
          tags: ['Economia', 'Sostenibilità'],
        },
      ],
    },
  ],
  default: [
    {
      id: 'ks-default',
      name: 'Knowledge base generica',
      type: 'Documenti',
      source: 'Database LMS',
      status: 'Attivo',
      lastUpdated: '01 Gen 2024',
      documentsCount: 25,
      tags: ['Generale'],
      description: "Raccolta generica di documenti per l'assistente.",
      sampleDocuments: [
        {
          id: 'd-default',
          title: 'Documento esempio',
          snippet: 'Contenuto di esempio...',
          tags: ['Esempio'],
        },
      ],
    },
  ],
}

export const mockToolsByAgent: Record<string, AgentTools> = {
  '3': {
    agentId: '3',
    tools: [
      {
        id: 't1',
        name: 'Ricerca web',
        key: 'web_search',
        description:
          'Cerca informazioni aggiornate sul web per rispondere a domande su argomenti recenti o non presenti nella knowledge base.',
        category: 'Ricerca',
        enabled: true,
        config: { maxResults: 5, timeoutMs: 3000, safeSearch: true },
        lastUsed: '14 Gen 2024',
        weeklyCallCount: 23,
      },
      {
        id: 't2',
        name: 'Leggi profilo studente',
        key: 'user_profile_reader',
        description:
          'Accede al profilo dello studente per personalizzare le risposte in base al livello di conoscenza e agli interessi.',
        category: 'Profilo utente',
        enabled: true,
        config: { includeHistory: true, maxHistoryItems: 10 },
        lastUsed: '15 Gen 2024',
        weeklyCallCount: 142,
      },
      {
        id: 't3',
        name: 'Calcola difficoltà contenuto',
        key: 'content_difficulty_analyzer',
        description:
          'Analizza la complessità di un testo o concetto per adattare la spiegazione al livello dello studente.',
        category: 'Analisi dati',
        enabled: true,
        config: { algorithm: 'flesch-kincaid', minScore: 0, maxScore: 100 },
        lastUsed: '15 Gen 2024',
        weeklyCallCount: 87,
      },
      {
        id: 't4',
        name: 'Invia notifica email',
        key: 'email_sender',
        description:
          'Invia email di riepilogo o promemoria allo studente quando richiesto.',
        category: 'Comunicazione',
        enabled: false,
        config: { maxEmailsPerDay: 3, requireConfirmation: true },
        lastUsed: '10 Gen 2024',
        weeklyCallCount: 5,
      },
      {
        id: 't5',
        name: 'Integrazione calendario',
        key: 'calendar_integration',
        description:
          'Accede al calendario dello studente per suggerire orari di studio o ricordare scadenze.',
        category: 'Integrazione',
        enabled: false,
        config: { provider: 'google', syncInterval: 3600 },
        lastUsed: '08 Gen 2024',
        weeklyCallCount: 2,
      },
    ],
  },
  default: {
    agentId: 'default',
    tools: [
      {
        id: 't-default-1',
        name: 'Ricerca web',
        key: 'web_search',
        description: 'Cerca informazioni sul web.',
        category: 'Ricerca',
        enabled: true,
        config: { maxResults: 5 },
        lastUsed: '01 Gen 2024',
        weeklyCallCount: 10,
      },
      {
        id: 't-default-2',
        name: 'Leggi profilo utente',
        key: 'user_profile_reader',
        description: 'Accede al profilo utente.',
        category: 'Profilo utente',
        enabled: false,
        config: { includeHistory: false },
        lastUsed: '28 Dic 2023',
        weeklyCallCount: 3,
      },
    ],
  },
}

export const mockWidgetsByAgent: Record<string, AgentWidgets> = {
  '3': {
    agentId: '3',
    widgets: [
      {
        id: 'w1',
        name: 'Checklist di attività',
        key: 'checklist',
        description:
          'Crea liste di attività interattive per aiutare lo studente a organizzare il proprio studio.',
        category: 'Azioni',
        enabled: true,
        previewExample: {
          title: 'Preparazione esame di Fisica',
          items: [
            { id: '1', text: 'Rivedere capitolo 3: Dinamica', checked: true },
            { id: '2', text: 'Risolvere esercizi pag. 45-50', checked: true },
            { id: '3', text: 'Studiare formule del moto', checked: false },
            { id: '4', text: 'Fare simulazione test finale', checked: false },
          ],
        },
      },
      {
        id: 'w2',
        name: 'Link con preview',
        key: 'link_preview',
        description:
          'Mostra link esterni con anteprima visuale (titolo, descrizione, immagine) per facilitare la navigazione.',
        category: 'Contenuti',
        enabled: true,
        previewExample: {
          url: 'https://example.com/corso-sostenibilita',
          title: 'Leadership Sostenibile - Corso Executive',
          description:
            'Impara i principi della leadership sostenibile applicati al business moderno.',
          thumbnail: true,
        },
      },
      {
        id: 'w3',
        name: 'Grafico semplice',
        key: 'chart',
        description:
          'Visualizza dati in forma grafica (barre, linee) per rendere le informazioni più comprensibili.',
        category: 'Visualizzazioni',
        enabled: true,
        previewExample: {
          title: 'Progressi settimanali',
          data: [
            { label: 'Lun', value: 65 },
            { label: 'Mar', value: 80 },
            { label: 'Mer', value: 45 },
            { label: 'Gio', value: 90 },
          ],
        },
      },
      {
        id: 'w4',
        name: 'Sondaggio',
        key: 'poll',
        description:
          'Crea sondaggi rapidi per raccogliere feedback o verificare la comprensione dello studente.',
        category: 'Azioni',
        enabled: false,
        previewExample: {
          question: "Quanto ti senti preparato per l'esame?",
          options: [
            { id: '1', text: 'Molto preparato' },
            { id: '2', text: 'Abbastanza preparato' },
            { id: '3', text: 'Poco preparato' },
            { id: '4', text: 'Per niente preparato' },
          ],
        },
      },
      {
        id: 'w5',
        name: 'Quote + link al contenuto',
        key: 'quote',
        description:
          'Evidenzia citazioni importanti dai materiali del corso con link diretto alla fonte.',
        category: 'Contenuti',
        enabled: true,
        previewExample: {
          text: 'La sostenibilità non è solo una scelta etica, ma una necessità strategica per le aziende moderne.',
          source: 'Leadership Sostenibile - Modulo 2',
          author: 'Prof. Marco Rossi',
        },
      },
      {
        id: 'w6',
        name: 'CTA per modifica profilo',
        key: 'cta_edit_profile',
        description:
          'Invita lo studente ad aggiornare il proprio profilo per ricevere consigli più personalizzati.',
        category: 'Azioni',
        enabled: false,
        previewExample: {
          title: 'Personalizza la tua esperienza',
          message:
            'Aggiorna le tue preferenze di apprendimento per ricevere contenuti su misura.',
          buttonText: 'Aggiorna il tuo profilo',
        },
      },
    ],
  },
  default: {
    agentId: 'default',
    widgets: [
      {
        id: 'w-default-1',
        name: 'Checklist di attività',
        key: 'checklist',
        description: 'Crea liste di attività.',
        category: 'Azioni',
        enabled: true,
        previewExample: {
          title: 'Attività da completare',
          items: [
            { id: '1', text: 'Task 1', checked: false },
            { id: '2', text: 'Task 2', checked: false },
          ],
        },
      },
      {
        id: 'w-default-2',
        name: 'Link con preview',
        key: 'link_preview',
        description: 'Mostra link con anteprima.',
        category: 'Contenuti',
        enabled: false,
        previewExample: {
          url: 'https://example.com',
          title: 'Esempio',
          description: 'Descrizione esempio',
          thumbnail: false,
        },
      },
    ],
  },
}

export const mockInteractionsByAgent: Record<string, AgentHistory> = {
  '3': {
    agentId: '3',
    interactions: [
      {
        id: 'int1',
        timestamp: '2024-01-15T14:32:00Z',
        userId: 'user123',
        userLabel: 'Marco Bianchi',
        userInput:
          "Come funziona il principio di conservazione dell'energia nella fisica classica?",
        behaviorSectionsUsed: ['Premessa V1', 'Logica V1', 'Output V1'],
        toolsUsed: ['user_profile_reader', 'content_difficulty_analyzer'],
        widgetsUsed: ['checklist', 'link_preview'],
        costEstimate: 12,
        finalTextOutput:
          "Il principio di conservazione dell'energia afferma che l'energia totale di un sistema isolato rimane costante nel tempo. In altre parole, l'energia non può essere creata né distrutta, ma solo trasformata da una forma all'altra.\n\nNella fisica classica, questo principio si applica a sistemi meccanici dove l'energia cinetica (energia del movimento) e l'energia potenziale (energia immagazzinata) si convertono continuamente l'una nell'altra, ma la loro somma rimane costante.",
        finalWidgetsOutput: [
          {
            type: 'checklist',
            payload: {
              title: 'Prossimi passi per approfondire',
              items: [
                {
                  id: '1',
                  text: 'Studiare esempi di trasformazione energetica',
                  checked: false,
                },
                {
                  id: '2',
                  text: 'Risolvere esercizi sul lavoro e energia',
                  checked: false,
                },
                {
                  id: '3',
                  text: 'Rivedere il concetto di forza conservativa',
                  checked: false,
                },
              ],
            },
          },
          {
            type: 'link_preview',
            payload: {
              url: 'https://example.com/fisica-energia',
              title: "Approfondimento: Conservazione dell'Energia",
              description: 'Video lezione con esempi pratici e dimostrazioni',
              thumbnail: true,
            },
          },
        ],
        traceSteps: [
          {
            id: 's1',
            label: 'Ricezione richiesta',
            description: 'Input utente ricevuto e validato',
            timestamp: '14:32:00',
          },
          {
            id: 's2',
            label: 'Classificazione argomento',
            description: 'Identificato: Fisica classica, livello intermedio',
            timestamp: '14:32:01',
          },
          {
            id: 's3',
            label: 'Lettura profilo studente',
            description:
              'Funzionalità: user_profile_reader - Livello studente: intermedio',
            timestamp: '14:32:02',
          },
          {
            id: 's4',
            label: 'Analisi difficoltà',
            description:
              'Funzionalità: content_difficulty_analyzer - Score: 65/100',
            timestamp: '14:32:03',
          },
          {
            id: 's5',
            label: 'Recupero conoscenza',
            description: 'Trovati 3 documenti rilevanti nel catalogo corsi',
            timestamp: '14:32:04',
          },
          {
            id: 's6',
            label: 'Generazione risposta',
            description: 'Risposta generata con spiegazione semplificata',
            timestamp: '14:32:06',
          },
          {
            id: 's7',
            label: 'Aggiunta widgets',
            description: 'Checklist e Link preview inseriti',
            timestamp: '14:32:07',
          },
        ],
      },
      {
        id: 'int2',
        timestamp: '2024-01-15T10:15:00Z',
        userId: 'user456',
        userLabel: 'Sofia Rossi',
        userInput:
          'Quali sono i corsi disponibili sulla sostenibilità aziendale?',
        behaviorSectionsUsed: ['Premessa V1', 'Logica V1', 'Output V1'],
        toolsUsed: ['web_search'],
        widgetsUsed: ['link_preview', 'quote'],
        costEstimate: 8,
        finalTextOutput:
          'Abbiamo diversi corsi sulla sostenibilità aziendale nel nostro catalogo 2025. I più rilevanti sono:\n\n1. **Leadership Sostenibile** - Un corso executive che esplora i principi della leadership sostenibile applicati al contesto aziendale moderno.\n\n2. **Economia Circolare per Manager** - Approfondisce i modelli di business circolari e le strategie di implementazione.\n\n3. **ESG e Reporting di Sostenibilità** - Focalizzato su metriche, standard e comunicazione della sostenibilità.',
        finalWidgetsOutput: [
          {
            type: 'link_preview',
            payload: {
              url: 'https://example.com/leadership-sostenibile',
              title: 'Leadership Sostenibile - Corso Executive',
              description:
                'Impara i principi della leadership sostenibile applicati al business moderno',
              thumbnail: true,
            },
          },
          {
            type: 'quote',
            payload: {
              text: 'La sostenibilità non è solo una scelta etica, ma una necessità strategica per le aziende moderne.',
              source: 'Leadership Sostenibile - Modulo 2',
              author: 'Prof. Marco Rossi',
            },
          },
        ],
        traceSteps: [
          {
            id: 's1',
            label: 'Ricezione richiesta',
            description: 'Input utente ricevuto',
            timestamp: '10:15:00',
          },
          {
            id: 's2',
            label: 'Classificazione',
            description: 'Richiesta informazioni corsi',
            timestamp: '10:15:01',
          },
          {
            id: 's3',
            label: 'Ricerca catalogo',
            description: 'Trovati 8 corsi sulla sostenibilità',
            timestamp: '10:15:02',
          },
          {
            id: 's4',
            label: 'Ricerca web',
            description:
              'Funzionalità: web_search - Informazioni aggiuntive recuperate',
            timestamp: '10:15:04',
          },
          {
            id: 's5',
            label: 'Composizione risposta',
            description: 'Lista corsi con descrizioni',
            timestamp: '10:15:05',
          },
        ],
      },
      {
        id: 'int3',
        timestamp: '2024-01-14T16:45:00Z',
        userId: 'user789',
        userLabel: 'Luca Verdi',
        userInput:
          "Ho bisogno di aiuto per organizzare lo studio per l'esame di matematica",
        behaviorSectionsUsed: ['Premessa V2', 'Logica V1', 'Output V1'],
        toolsUsed: ['user_profile_reader'],
        widgetsUsed: ['checklist', 'chart'],
        costEstimate: 10,
        finalTextOutput:
          "Perfetto! Ti aiuto a organizzare lo studio per l'esame di matematica. Ho creato un piano di studio personalizzato basato sul tuo profilo e sul tempo disponibile.",
        finalWidgetsOutput: [
          {
            type: 'checklist',
            payload: {
              title: 'Piano di studio - Settimana 1',
              items: [
                {
                  id: '1',
                  text: 'Ripassare algebra lineare (Cap. 1-3)',
                  checked: false,
                },
                {
                  id: '2',
                  text: 'Esercizi su matrici e determinanti',
                  checked: false,
                },
                {
                  id: '3',
                  text: 'Studiare calcolo differenziale',
                  checked: false,
                },
                {
                  id: '4',
                  text: 'Simulazione test intermedio',
                  checked: false,
                },
              ],
            },
          },
          {
            type: 'chart',
            payload: {
              title: 'Tempo consigliato per argomento (ore)',
              data: [
                { label: 'Algebra', value: 8 },
                { label: 'Calcolo', value: 10 },
                { label: 'Geometria', value: 6 },
                { label: 'Esercizi', value: 12 },
              ],
            },
          },
        ],
        traceSteps: [
          {
            id: 's1',
            label: 'Ricezione richiesta',
            description: 'Richiesta organizzazione studio',
            timestamp: '16:45:00',
          },
          {
            id: 's2',
            label: 'Lettura profilo',
            description:
              'Funzionalità: user_profile_reader - Esame: Matematica 2',
            timestamp: '16:45:01',
          },
          {
            id: 's3',
            label: 'Analisi calendario',
            description: 'Tempo disponibile: 4 settimane',
            timestamp: '16:45:02',
          },
          {
            id: 's4',
            label: 'Generazione piano',
            description: 'Piano personalizzato creato',
            timestamp: '16:45:04',
          },
        ],
      },
    ],
  },
  default: {
    agentId: 'default',
    interactions: [
      {
        id: 'int-default',
        timestamp: '2024-01-10T12:00:00Z',
        userId: 'user000',
        userLabel: 'Utente Test',
        userInput: 'Ciao, come stai?',
        behaviorSectionsUsed: ['Premessa V1', 'Logica V1', 'Output V1'],
        toolsUsed: [],
        widgetsUsed: [],
        costEstimate: 3,
        finalTextOutput: 'Ciao! Sto bene, grazie. Come posso aiutarti oggi?',
        finalWidgetsOutput: [],
        traceSteps: [
          {
            id: 's1',
            label: 'Ricezione',
            description: 'Input ricevuto',
            timestamp: '12:00:00',
          },
          {
            id: 's2',
            label: 'Risposta',
            description: 'Risposta generata',
            timestamp: '12:00:01',
          },
        ],
      },
    ],
  },
}

// Mock Cost Data
export const mockCostData: CostData = {
  costPerAgent: [
    {
      agentId: '3',
      agentName: 'Service 3',
      month: 'Gennaio 2024',
      totalCostEur: 45.8,
    },
    {
      agentId: '2',
      agentName: 'Service 2',
      month: 'Gennaio 2024',
      totalCostEur: 32.5,
    },
    {
      agentId: '1',
      agentName: 'Service 1',
      month: 'Gennaio 2024',
      totalCostEur: 28.3,
    },
    {
      agentId: '7',
      agentName: 'Service 7',
      month: 'Gennaio 2024',
      totalCostEur: 18.9,
    },
    {
      agentId: '4',
      agentName: 'Service 4',
      month: 'Gennaio 2024',
      totalCostEur: 12.4,
    },
  ],
  conversationCosts: [
    {
      id: 'conv1',
      agentName: 'Service 3',
      userId: 'user123',
      userLabel: 'Marco Bianchi',
      timestamp: '2024-01-15T14:32:00Z',
      costEur: 0.12,
      requests: [
        {
          id: 'call1',
          timestamp: '14:32:01',
          operation: 'Classificazione input',
          costEur: 0.02,
          responseLength: 150,
        },
        {
          id: 'call2',
          timestamp: '14:32:02',
          operation: 'Lettura profilo utente',
          costEur: 0.01,
          responseLength: 80,
        },
        {
          id: 'call3',
          timestamp: '14:32:04',
          operation: 'Ricerca knowledge base',
          costEur: 0.03,
          responseLength: 250,
        },
        {
          id: 'call4',
          timestamp: '14:32:06',
          operation: 'Generazione risposta',
          costEur: 0.05,
          responseLength: 420,
        },
        {
          id: 'call5',
          timestamp: '14:32:07',
          operation: 'Creazione widgets',
          costEur: 0.01,
          responseLength: 90,
        },
      ],
    },
    {
      id: 'conv2',
      agentName: 'Service 2',
      userId: 'user456',
      userLabel: 'Sofia Rossi',
      timestamp: '2024-01-15T11:20:00Z',
      costEur: 0.08,
      requests: [
        {
          id: 'call1',
          timestamp: '11:20:01',
          operation: 'Analisi richiesta',
          costEur: 0.02,
          responseLength: 120,
        },
        {
          id: 'call2',
          timestamp: '11:20:03',
          operation: 'Ricerca web',
          costEur: 0.04,
          responseLength: 300,
        },
        {
          id: 'call3',
          timestamp: '11:20:05',
          operation: 'Composizione risposta',
          costEur: 0.02,
          responseLength: 180,
        },
      ],
    },
    {
      id: 'conv3',
      agentName: 'Service 3',
      userId: 'user789',
      userLabel: 'Luca Verdi',
      timestamp: '2024-01-14T16:45:00Z',
      costEur: 0.1,
      requests: [
        {
          id: 'call1',
          timestamp: '16:45:01',
          operation: 'Lettura profilo',
          costEur: 0.01,
          responseLength: 100,
        },
        {
          id: 'call2',
          timestamp: '16:45:02',
          operation: 'Analisi calendario',
          costEur: 0.02,
          responseLength: 150,
        },
        {
          id: 'call3',
          timestamp: '16:45:04',
          operation: 'Generazione piano studio',
          costEur: 0.06,
          responseLength: 480,
        },
        {
          id: 'call4',
          timestamp: '16:45:05',
          operation: 'Creazione checklist',
          costEur: 0.01,
          responseLength: 70,
        },
      ],
    },
    {
      id: 'conv4',
      agentName: 'Service 1',
      userId: 'user234',
      userLabel: 'Anna Neri',
      timestamp: '2024-01-14T09:15:00Z',
      costEur: 0.15,
      requests: [
        {
          id: 'call1',
          timestamp: '09:15:01',
          operation: 'Caricamento profilo',
          costEur: 0.02,
          responseLength: 200,
        },
        {
          id: 'call2',
          timestamp: '09:15:03',
          operation: 'Analisi affinità',
          costEur: 0.08,
          responseLength: 650,
        },
        {
          id: 'call3',
          timestamp: '09:15:05',
          operation: 'Ranking risultati',
          costEur: 0.03,
          responseLength: 280,
        },
        {
          id: 'call4',
          timestamp: '09:15:06',
          operation: 'Formattazione output',
          costEur: 0.02,
          responseLength: 150,
        },
      ],
    },
  ],
  userLimits: [
    {
      userId: 'user123',
      userLabel: 'Marco Bianchi',
      currentMonthCostEur: 8.5,
      limitEur: 50,
    },
    {
      userId: 'user456',
      userLabel: 'Sofia Rossi',
      currentMonthCostEur: 12.3,
      limitEur: 50,
    },
    {
      userId: 'user789',
      userLabel: 'Luca Verdi',
      currentMonthCostEur: 6.8,
      limitEur: 50,
    },
    {
      userId: 'user234',
      userLabel: 'Anna Neri',
      currentMonthCostEur: 15.2,
      limitEur: 50,
    },
  ],
  globalMonthlyLimitPerUser: 50,
  totalRequests: 1247,
}

// Hourly activity data for overview charts
export const mockHourlyActivity = [
  { hour: '00:00', calls: 12 },
  { hour: '01:00', calls: 8 },
  { hour: '02:00', calls: 5 },
  { hour: '03:00', calls: 3 },
  { hour: '04:00', calls: 4 },
  { hour: '05:00', calls: 7 },
  { hour: '06:00', calls: 15 },
  { hour: '07:00', calls: 28 },
  { hour: '08:00', calls: 45 },
  { hour: '09:00', calls: 68 },
  { hour: '10:00', calls: 82 },
  { hour: '11:00', calls: 75 },
  { hour: '12:00', calls: 58 },
  { hour: '13:00', calls: 52 },
  { hour: '14:00', calls: 71 },
  { hour: '15:00', calls: 88 },
  { hour: '16:00', calls: 92 },
  { hour: '17:00', calls: 78 },
  { hour: '18:00', calls: 54 },
  { hour: '19:00', calls: 38 },
  { hour: '20:00', calls: 25 },
  { hour: '21:00', calls: 18 },
  { hour: '22:00', calls: 15 },
  { hour: '23:00', calls: 10 },
]
